/**
 * タイムラインデータ統合ライブラリ
 *
 * works-ui.json と events-ui.json を統合してタイムライン表示用データを生成する
 */

import type { Work, Event, TimelineItem } from '../types/domain';
import { compareDates, extractYear } from './date-formatters';
import { getLocalizedText, type SupportedLanguage } from './i18n-helpers';

const DIGIT_START_RE = /^\d/;

/**
 * イベントが期間イベント（開始日と終了日を持つ）かどうかを判定
 *
 * @param event - イベントオブジェクト
 * @returns 期間イベントの場合true
 */
export function isPeriodEvent(event: Event): boolean {
  return !!(event.startDate && event.endDate);
}

/**
 * URIから最後のパス部分（slug）を抽出
 *
 * @param uri - URI文字列（例: "https://takun-archive.org/work/work-1966-barabara-manga-collection"）
 * @returns slug（例: "work-1966-barabara-manga-collection"）
 */
function extractSlugFromUri(uri: string): string {
  const parts = uri.split('/');
  return parts[parts.length - 1] || uri;
}

/**
 * WorkをTimelineItemに変換
 *
 * @param work - 作品オブジェクト
 * @returns タイムラインアイテム、または不正な日付の場合はnull
 */
function workToTimelineItem(work: Work): TimelineItem | null {
  // 日付の妥当性チェック
  if (!work.dateCreated || !work.dateCreated.trim()) {
    return null;
  }

  // 数字以外で始まる日付は無効（"年数不明"など）
  if (!DIGIT_START_RE.test(work.dateCreated)) {
    return null;
  }

  const slug = extractSlugFromUri(work.id);
  return {
    id: work.id,
    type: 'work',
    title: work.title || '',
    date: work.dateCreated,
    link: `/works/${slug}`,
    data: work,
  };
}

/**
 * EventをTimelineItemに変換
 *
 * @param event - イベントオブジェクト
 * @returns タイムラインアイテム、または不正な日付の場合はnull
 */
function eventToTimelineItem(event: Event): TimelineItem | null {
  // 日付の妥当性チェック
  if (!event.startDate || !event.startDate.trim()) {
    return null;
  }

  // 数字以外で始まる日付は無効
  if (!DIGIT_START_RE.test(event.startDate)) {
    return null;
  }

  const slug = extractSlugFromUri(event.id);
  return {
    id: event.id,
    type: 'event',
    title: event.title || '',
    date: event.startDate,
    link: `/events/${slug}`,
    data: event,
  };
}

/**
 * 作品とイベントを統合してタイムラインアイテムに変換
 *
 * @param works - 作品リスト
 * @param events - イベントリスト
 * @returns 日付順にソートされたタイムラインアイテムリスト
 */
export function mergeWorksAndEvents(
  works: Work[],
  events: Event[]
): TimelineItem[] {
  const timelineItems: TimelineItem[] = [];

  // Workを変換（nullは除外）
  for (const work of works) {
    try {
      const item = workToTimelineItem(work);
      if (item !== null) {
        timelineItems.push(item);
      }
    } catch (error) {
      console.error(
        `Failed to convert work to timeline item: ${work.id}`,
        error
      );
    }
  }

  // Eventを変換（nullは除外）
  for (const event of events) {
    try {
      const item = eventToTimelineItem(event);
      if (item !== null) {
        timelineItems.push(item);
      }
    } catch (error) {
      console.error(
        `Failed to convert event to timeline item: ${event.id}`,
        error
      );
    }
  }

  // 日付でソート（昇順）。年代表記（"1970年代"）を含む全形式に対応
  timelineItems.sort((a, b) => compareDates(a.date, b.date));

  return timelineItems;
}

/**
 * タイムラインアイテムから年の範囲を取得
 *
 * @param items - タイムラインアイテムリスト
 * @returns [最小年, 最大年] のタプル。データがない場合は [現在年-10, 現在年]
 */
export function getTimelineYearRange(items: TimelineItem[]): [number, number] {
  if (items.length === 0) {
    const currentYear = new Date().getFullYear();
    return [currentYear - 10, currentYear];
  }

  let minYear = Infinity;
  let maxYear = -Infinity;

  for (const item of items) {
    const year = extractYear(item.date);
    if (year !== null) {
      if (year < minYear) minYear = year;
      if (year > maxYear) maxYear = year;
    }
  }

  // 妥当性チェック
  if (!isFinite(minYear) || !isFinite(maxYear)) {
    const currentYear = new Date().getFullYear();
    return [currentYear - 10, currentYear];
  }

  return [minYear, maxYear];
}

/**
 * TimelineItemからジャンルを取得
 *
 * WorkとEventでgenreの形式が異なるため、
 * 統一的にstring型で返す。
 *
 * @param item - タイムラインアイテム
 * @param language - 言語コード
 * @returns ジャンル文字列、存在しない場合はundefined
 */
export function getTimelineItemGenre(
  item: TimelineItem,
  language: SupportedLanguage
): string | undefined {
  if (item.type === 'work') {
    // Workのgenreはstring型
    const work = item.data as Work;
    return work.genre;
  } else {
    // Eventのgenreはstring | LanguageMap型
    const event = item.data as Event;
    const genre = event.eventType;
    if (!genre) return undefined;
    return getLocalizedText(genre, language);
  }
}

/**
 * タイムラインアイテムからジャンル一覧を抽出
 *
 * @param items - タイムラインアイテムリスト
 * @param language - 言語コード
 * @returns ソートされたジャンル一覧
 */
export function extractGenresFromTimelineItems(
  items: TimelineItem[],
  language: SupportedLanguage
): string[] {
  const genres = new Set<string>();

  for (const item of items) {
    const genre = getTimelineItemGenre(item, language);
    if (genre) {
      genres.add(genre);
    }
  }

  return Array.from(genres).sort();
}

/**
 * タイムラインアイテムから利用可能な10年単位の年代を抽出
 *
 * @param items - タイムラインアイテムリスト
 * @returns 10年単位の開始年の配列（昇順）。例: [1960, 1970, 1980, ...]
 */
export function getAvailableDecades(items: TimelineItem[]): number[] {
  const decades = new Set<number>();

  for (const item of items) {
    const year = extractYear(item.date);
    if (year !== null) {
      // 10年単位の開始年を計算（例: 1966 → 1960）
      const decade = Math.floor(year / 10) * 10;
      decades.add(decade);
    }
  }

  return Array.from(decades).sort((a, b) => a - b);
}
